import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  ShieldCheck,
  Mail,
  MailOpen,
  Loader2,
  RefreshCw,
  Image as ImageIcon,
  Reply,
  Send,
  ChevronDown,
  ChevronRight,
  User as UserIcon,
  Heart,
  ThumbsUp,
  X,
} from "lucide-react";
import {
  getArchitectInbox,
  markArchitectDmRead,
  replyArchitectDm,
  reactArchitectDm,
  uploadArchitectDmImage,
  architectDmAttachmentUrl,
} from "../lib/architectDm";
import { useAuth } from "../contexts/AuthContext";

function formatWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const bodyMdComponents = {
  a: ({ node, ...props }) => (
    <a {...props} target="_blank" rel="noreferrer noopener" />
  ),
};

const REACTIONS = [
  { emoji: "heart", Icon: Heart, label: "love" },
  { emoji: "thumb", Icon: ThumbsUp, label: "like" },
];

function ReactionBar({ msg, meUserId, onReacted }) {
  const [busy, setBusy] = useState(null);
  const toggle = async (emoji) => {
    if (busy) return;
    setBusy(emoji);
    try {
      const res = await reactArchitectDm(msg.id, emoji);
      onReacted?.(msg.id, res.message);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Reaction failed");
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="mt-2 flex items-center gap-1.5">
      {REACTIONS.map(({ emoji, Icon, label }) => {
        const r = msg.reactions?.[emoji];
        const count = r?.count || 0;
        const mine = Array.isArray(r?.user_ids) && r.user_ids.includes(meUserId);
        return (
          <button
            key={emoji}
            type="button"
            onClick={() => toggle(emoji)}
            disabled={busy === emoji}
            data-testid={`inbox-react-${emoji}-${msg.id}`}
            aria-label={label}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] font-mono transition-colors ${
              mine
                ? "border-rose-500/60 bg-rose-500/10 text-rose-300"
                : "border-zinc-800 text-zinc-500 hover:text-rose-300 hover:border-rose-500/40"
            } ${busy === emoji ? "opacity-50" : ""}`}
          >
            <Icon
              className={`h-3 w-3 ${mine ? "fill-current" : ""}`}
            />
            {count > 0 && <span>{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

function MessageBubble({ msg, meUserId, onReacted }) {
  const mine = msg.sender_user_id === meUserId;
  return (
    <div
      data-testid={`inbox-msg-${msg.id}`}
      className={`border ${
        mine
          ? "border-emerald-500/30 bg-emerald-500/[0.03] ml-6"
          : "border-amber-500/30 bg-amber-500/[0.03] mr-6"
      } p-3`}
    >
      <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-[0.25em] font-mono">
        <span
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 border ${
            mine
              ? "border-emerald-500/50 text-emerald-300 bg-emerald-500/5"
              : "border-amber-500/50 text-amber-300 bg-amber-500/5"
          }`}
        >
          {mine ? (
            <UserIcon className="h-3 w-3" />
          ) : (
            <ShieldCheck className="h-3 w-3" />
          )}
          {mine ? "You" : msg.sender_name || "The Architect"}
        </span>
        <span className="text-zinc-600 ml-auto">
          {formatWhen(msg.created_at)}
        </span>
      </div>
      {msg.title && !msg.title.toLowerCase().startsWith("re:") && (
        <div className="mt-1.5 text-sm text-zinc-100">{msg.title}</div>
      )}
      <div
        data-testid={`inbox-body-${msg.id}`}
        className="mt-1 prose prose-invert prose-sm max-w-none text-zinc-200 leading-relaxed prose-a:text-amber-300 hover:prose-a:text-amber-200 prose-strong:text-zinc-100 prose-code:text-amber-200 prose-code:bg-zinc-900 prose-code:px-1 prose-code:rounded-sm prose-headings:text-zinc-100 prose-hr:border-zinc-800 prose-blockquote:border-l-amber-500/40 prose-blockquote:text-zinc-400"
      >
        <ReactMarkdown components={bodyMdComponents}>
          {msg.body || ""}
        </ReactMarkdown>
      </div>
      {msg.image_path && (
        <img
          src={architectDmAttachmentUrl(msg.image_path)}
          alt="attachment"
          className="mt-2 max-h-[400px] border border-zinc-800"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      )}
      <ReactionBar msg={msg} meUserId={meUserId} onReacted={onReacted} />
    </div>
  );
}

function ThreadCard({ thread, meUserId, onReplied, onOpenChanged }) {
  const [open, setOpen] = useState(thread.hasUnread);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [replyImage, setReplyImage] = useState(null); // {path, content_type, previewUrl, name}
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    // auto-mark all incoming unread messages read on open
    if (!open) return;
    thread.messages
      .filter(
        (m) => m.sender_user_id !== meUserId && !m.read_at
      )
      .forEach((m) => {
        markArchitectDmRead(m.id)
          .then(() => onReplied?.(m.id, { read_at: new Date().toISOString() }))
          .catch(() => {
            /* silent */
          });
      });
    onOpenChanged?.(thread.rootId, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadArchitectDmImage(f);
      setReplyImage({
        path: res.path,
        content_type: res.content_type,
        previewUrl: URL.createObjectURL(f),
        name: f.name,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submitReply = async () => {
    const body = replyBody.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const res = await replyArchitectDm(
        thread.rootId,
        body,
        replyImage?.path || null,
        replyImage?.content_type || null
      );
      onReplied?.(null, { newMessage: res.message });
      setReplyBody("");
      setReplyImage(null);
      setReplyOpen(false);
      toast.success("Reply sent");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Reply failed");
    } finally {
      setSending(false);
    }
  };

  const rootMsg =
    thread.messages.find((m) => m.id === thread.rootId) ||
    thread.messages[0];
  const rootTitle = rootMsg?.title || "(no title)";
  // Counterparty = the other side of the conversation (not me).
  const counterparty = (() => {
    for (const m of thread.messages) {
      if (m.sender_user_id !== meUserId) {
        return {
          email: m.sender_email,
          user_id: m.sender_user_id,
          name: m.sender_name,
        };
      }
      if (
        m.recipient_user_id &&
        m.recipient_user_id !== meUserId
      ) {
        return {
          email: m.recipient_email,
          user_id: m.recipient_user_id,
          name: m.recipient_name,
        };
      }
    }
    return { email: rootMsg?.recipient_email, user_id: rootMsg?.recipient_user_id };
  })();
  const iSentRoot = rootMsg?.sender_user_id === meUserId;
  const counterLabel =
    counterparty.email || counterparty.name || counterparty.user_id || "unknown";

  return (
    <article
      data-testid={`inbox-thread-${thread.rootId}`}
      className={`border ${
        thread.hasUnread ? "border-amber-500/40" : "border-zinc-800"
      } bg-[#0b0b0d]`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-3 sm:p-4 flex items-center gap-3"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-zinc-500 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-zinc-500 shrink-0" />
        )}
        <div
          className={`shrink-0 h-6 w-6 border flex items-center justify-center ${
            thread.hasUnread
              ? "border-amber-500/50 text-amber-300 bg-amber-500/10"
              : "border-zinc-800 text-zinc-500"
          }`}
        >
          {thread.hasUnread ? (
            <Mail className="h-3.5 w-3.5" />
          ) : (
            <MailOpen className="h-3.5 w-3.5" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500">
            <span className="text-zinc-600">{iSentRoot ? "to" : "from"}</span>
            <span
              data-testid={`inbox-thread-counterparty-${thread.rootId}`}
              className="text-amber-300 normal-case tracking-normal font-medium truncate"
            >
              {counterLabel}
            </span>
            {thread.hasUnread && (
              <span className="text-amber-300">· unread</span>
            )}
          </div>
          <div className="text-sm text-zinc-100 truncate mt-0.5">
            {rootTitle}
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500 mt-0.5 flex items-center gap-2">
            <span>{thread.messages.length} message{thread.messages.length === 1 ? "" : "s"}</span>
            <span className="text-zinc-700">·</span>
            <span>{formatWhen(thread.latestAt)}</span>
          </div>
        </div>
      </button>
      {open && (
        <div className="border-t border-zinc-900 p-3 sm:p-4 space-y-2">
          {thread.messages.map((m) => (
            <MessageBubble
              key={m.id}
              msg={m}
              meUserId={meUserId}
              onReacted={(id, updated) =>
                onReplied?.(null, { updatedMessage: updated })
              }
            />
          ))}
          {!replyOpen ? (
            <button
              type="button"
              onClick={() => setReplyOpen(true)}
              data-testid={`inbox-reply-open-${thread.rootId}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-[11px] uppercase tracking-[0.25em] font-mono"
            >
              <Reply className="h-3 w-3" /> reply
            </button>
          ) : (
            <div className="space-y-2 border border-emerald-500/30 bg-emerald-500/[0.03] p-3">
              <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-emerald-300 flex items-center gap-2">
                <Reply className="h-3 w-3" /> your reply
                <span className="text-zinc-600 normal-case tracking-normal ml-2">
                  markdown supported · attach an image
                </span>
              </div>
              <textarea
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value.slice(0, 4000))}
                rows={4}
                placeholder="Write your reply…"
                data-testid={`inbox-reply-body-${thread.rootId}`}
                className="w-full px-3 py-2 bg-transparent border border-zinc-800 text-[13px] font-mono text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/50 resize-y"
              />
              <div className="flex items-center gap-2 flex-wrap">
                {!replyImage ? (
                  <label
                    data-testid={`inbox-reply-image-picker-${thread.rootId}`}
                    className="inline-flex items-center gap-1.5 px-2 py-1 border border-zinc-800 text-zinc-400 hover:text-emerald-300 hover:border-emerald-500/40 text-[10px] uppercase tracking-[0.25em] font-mono cursor-pointer"
                  >
                    {uploading ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <ImageIcon className="h-3 w-3" />
                    )}
                    {uploading ? "uploading…" : "attach image"}
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                      onChange={onPickImage}
                      disabled={uploading}
                    />
                  </label>
                ) : (
                  <div
                    data-testid={`inbox-reply-image-preview-${thread.rootId}`}
                    className="flex items-center gap-2 border border-emerald-500/30 bg-emerald-500/5 px-2 py-1"
                  >
                    <img
                      src={replyImage.previewUrl}
                      alt="attachment preview"
                      className="h-8 w-8 object-cover border border-zinc-800"
                    />
                    <div className="text-[10px] font-mono text-zinc-400">
                      <div className="truncate max-w-[140px]">
                        {replyImage.name}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setReplyImage(null)}
                      className="text-zinc-500 hover:text-rose-300"
                      data-testid={`inbox-reply-image-clear-${thread.rootId}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                <span className="text-[10px] font-mono text-zinc-600">
                  png · jpeg · webp · gif · 5 MB max
                </span>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={submitReply}
                  disabled={!replyBody.trim() || sending || uploading}
                  data-testid={`inbox-reply-send-${thread.rootId}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-emerald-500/50 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed text-[11px] uppercase tracking-[0.25em] font-mono"
                >
                  {sending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Send className="h-3 w-3" />
                  )}
                  send
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setReplyOpen(false);
                    setReplyBody("");
                    setReplyImage(null);
                  }}
                  disabled={sending}
                  className="text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500 hover:text-zinc-300"
                >
                  cancel
                </button>
                <span className="ml-auto text-[10px] font-mono text-zinc-600">
                  {replyBody.length}/4000
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function Inbox() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getArchitectInbox(200);
      setMessages(d?.messages || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load inbox.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const threads = useMemo(() => {
    // group by thread_root_id
    const meUid = user?.user_id;
    const map = new Map();
    for (const m of messages) {
      const key = m.thread_root_id || m.id;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(m);
    }
    return Array.from(map.entries())
      .map(([rootId, msgs]) => {
        // chronological within a thread
        msgs.sort((a, b) =>
          (a.created_at || "").localeCompare(b.created_at || "")
        );
        const latest = msgs[msgs.length - 1];
        const hasUnread = msgs.some(
          (m) => m.sender_user_id !== meUid && !m.read_at
        );
        return {
          rootId,
          messages: msgs,
          latestAt: latest.created_at,
          hasUnread,
        };
      })
      .sort((a, b) => (b.latestAt || "").localeCompare(a.latestAt || ""));
  }, [messages, user?.user_id]);

  if (!user?.user_id || String(user.user_id).startsWith("anon_")) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <p className="text-sm text-zinc-400">
          Sign in to see messages from the Architect.
        </p>
      </div>
    );
  }

  const unread = threads.filter((t) => t.hasUnread).length;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-5">
      <header className="flex items-center gap-3 flex-wrap">
        <ShieldCheck className="h-5 w-5 text-amber-400" />
        <h1 className="font-heading text-lg tracking-[0.25em] text-zinc-100">
          INBOX
        </h1>
        <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
          {unread} unread · {threads.length} thread{threads.length === 1 ? "" : "s"}
        </span>
        <button
          type="button"
          onClick={load}
          className="ml-auto inline-flex items-center gap-1 px-2 py-1 border border-zinc-800 text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-amber-300 hover:border-amber-500/40 font-mono"
          data-testid="inbox-refresh"
        >
          <RefreshCw className="h-3 w-3" /> refresh
        </button>
      </header>
      {user?.is_architect && (
        <Link
          to="/architect/messages"
          data-testid="inbox-compose-link"
          className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 text-[11px] uppercase tracking-[0.25em] font-mono"
        >
          Compose a message →
        </Link>
      )}
      {loading && (
        <div className="flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> loading…
        </div>
      )}
      {error && (
        <div className="text-rose-300 text-sm border border-rose-500/40 bg-rose-500/5 p-3">
          {error}
        </div>
      )}
      {!loading && !error && threads.length === 0 && (
        <p className="text-sm text-zinc-500">
          No messages yet. The Architect's notes will show up here.
        </p>
      )}
      <div className="space-y-2">
        {threads.map((t) => (
          <ThreadCard
            key={t.rootId}
            thread={t}
            meUserId={user.user_id}
            onReplied={(id, patch) => {
              if (patch?.newMessage) {
                setMessages((prev) => [patch.newMessage, ...prev]);
              } else if (patch?.updatedMessage) {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === patch.updatedMessage.id ? patch.updatedMessage : m
                  )
                );
              } else if (id && patch?.read_at) {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === id ? { ...m, read_at: patch.read_at } : m
                  )
                );
              }
            }}
          />
        ))}
      </div>
    </div>
  );
}
